import React, { useState, useEffect, useCallback } from "react";
import {
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Calendar,
  User,
  CreditCard,
  RefreshCw,
  Download,
  Loader2,
  Plus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { payoutTransactionService } from "../../lib/payoutTransactionService";
import { wiseWebhookService } from "../../lib/wiseWebhookService";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";
import { formatCurrency, getDefaultDateFilterState } from "../../utils/helpers";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";
import Header from "../Header";
import CustomDateRangePicker from "../CustomDateRangePicker";
import ManualPayoutRecorder from "./ManualPayoutRecorder";
import Papa from "papaparse";

const PAGE_SIZE = 25;

const PayoutRequesters = () => {
  const { user, isAdmin, isOwner } = useAuth();
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [allPayoutRequests, setAllPayoutRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Non-error outcome banner: { type: "success" | "warning", message }
  const [notice, setNotice] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [declinePayoutId, setDeclinePayoutId] = useState(null);
  const [showManualPaidConfirm, setShowManualPaidConfirm] = useState(false);
  const [manualPaidRequest, setManualPaidRequest] = useState(null);
  // DB-computed settleability (get_open_payout_requests): id -> { settleable,
  // settle_block_reason }. null = RPC unavailable, fall back to the legacy
  // client-side wise_transaction_id gate. The client column alone inverts the
  // server rule for 'processing' rows (unbound processing = runner-owned =
  // NOT settleable; dead-bound processing IS settleable), so the button must
  // mirror the server's verdict, not approximate it.
  const [settleInfo, setSettleInfo] = useState(null);

  // Webhook status tracking
  const [transferStatuses, setTransferStatuses] = useState({});
  const [statusHistory, setStatusHistory] = useState({});
  const [showStatusHistory, setShowStatusHistory] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState(null);

  // Real-time subscription tracking
  const [realtimeSubscription, setRealtimeSubscription] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);

  // Filter state - using DateRangePicker format.
  // Default to "all" so an admin opening Payout Requests sees every pending
  // request regardless of date (a single-day default silently hides older
  // pending rows even while the counter shows them). singleDate still seeds the
  // picker with today for when they switch to single-day mode.
  const [dateFilterType, setDateFilterType] = useState("all");
  const [singleDate, setSingleDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all"); // Default to all status
  const [showManualPayout, setShowManualPayout] = useState(false);
  const [filteredRequests, setFilteredRequests] = useState([]);

  // Payout mode (manual vs automatic) state
  const [payoutMode, setPayoutMode] = useState("manual");
  const [modeLoading, setModeLoading] = useState(false);

  // Load transfer statuses for requests with transfer IDs
  const loadTransferStatuses = useCallback(async (requests) => {
    try {
      const requestsWithTransfers = requests.filter(
        (request) => request.transfer_id
      );

      if (requestsWithTransfers.length === 0) {
        console.log("📊 No requests with transfer IDs found");
        return;
      }

      console.log(
        `📊 Loading transfer statuses for ${requestsWithTransfers.length} requests`
      );

      const statusPromises = requestsWithTransfers.map(async (request) => {
        const result = await wiseWebhookService.getTransferStatus(
          request.transfer_id
        );
        if (result.success && result.data) {
          return {
            transferId: request.transfer_id,
            payoutId: request.id,
            status: wiseWebhookService.formatTransferStatus(result.data),
          };
        }
        return null;
      });

      const statuses = await Promise.all(statusPromises);
      const statusMap = {};

      statuses.filter(Boolean).forEach(({ transferId, payoutId, status }) => {
        statusMap[transferId] = status;
      });

      setTransferStatuses(statusMap);
      console.log(
        `✅ Loaded ${Object.keys(statusMap).length} transfer statuses`
      );
    } catch (error) {
      console.error("❌ Error loading transfer statuses:", error);
    }
  }, []);

  const applyFilters = useCallback(
    (requests, page = 1) => {
      let filtered = requests;

      // Filter by date using DateRangePicker format
      if (dateFilterType === "single" && singleDate) {
        filtered = filtered.filter((request) => {
          const requestDate = new Date(request.request_date)
            .toISOString()
            .split("T")[0];
          return requestDate === singleDate;
        });
      } else if (dateFilterType === "range" && dateRangeStart && dateRangeEnd) {
        filtered = filtered.filter((request) => {
          const requestDate = new Date(request.request_date)
            .toISOString()
            .split("T")[0];
          return requestDate >= dateRangeStart && requestDate <= dateRangeEnd;
        });
      }
      // If dateFilterType is "all", don't filter by date

      // Filter by status
      if (selectedStatus && selectedStatus !== "all") {
        if (selectedStatus === "approved") {
          // When "approved" is selected, show both "approved" and "paid" transactions
          filtered = filtered.filter(
            (request) =>
              request.status === "approved" || request.status === "paid"
          );
        } else {
          // For other statuses, filter normally
          filtered = filtered.filter(
            (request) => request.status === selectedStatus
          );
        }
      }

      setFilteredRequests(filtered);
      setTotalRequests(filtered.length);

      // Implement client-side pagination on filtered results
      const startIndex = (page - 1) * PAGE_SIZE;
      const endIndex = startIndex + PAGE_SIZE;
      const paginatedRequests = filtered.slice(startIndex, endIndex);

      setPayoutRequests(paginatedRequests);
      setCurrentPage(page);

      console.log(
        `📄 Pagination: Page ${page}, showing ${paginatedRequests.length} of ${filtered.length} total filtered requests`
      );
    },
    [dateFilterType, singleDate, dateRangeStart, dateRangeEnd, selectedStatus]
  );

  // Extract available dates from payout requests
  const extractAvailableDates = useCallback((requests) => {
    const dates = new Set();

    requests.forEach((request) => {
      if (request.request_date) {
        // Convert to YYYY-MM-DD format for consistency with DateRangePicker
        const dateStr = new Date(request.request_date)
          .toISOString()
          .split("T")[0];
        dates.add(dateStr);
      }
    });

    const sortedDates = Array.from(dates).sort();
    console.log(
      `📅 PayoutRequesters: Found ${sortedDates.length} unique dates with payout requests`
    );

    return sortedDates;
  }, []);

  const loadPayoutRequests = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);

      try {
        const result = await payoutTransactionService.getAllPayoutRequests();

        if (result.success) {
          const allRequests = result.data;
          setAllPayoutRequests(allRequests);

          // Extract and set available dates for calendar highlighting
          const dates = extractAvailableDates(allRequests);
          setAvailableDates(dates);

          // Load webhook status for requests that have transfer IDs
          await loadTransferStatuses(allRequests);

          // Load DB-computed settleability for open rows (non-fatal: on
          // error keep null and the buttons fall back to the legacy gate;
          // record_manual_payout re-validates server-side regardless).
          try {
            const openResult =
              await payoutTransactionService.getOpenPayoutRequests();
            if (openResult.success) {
              const infoMap = {};
              for (const row of openResult.data) {
                infoMap[row.id] = {
                  settleable: row.settleable === true,
                  settle_block_reason: row.settle_block_reason || null,
                };
              }
              setSettleInfo(infoMap);
            } else {
              setSettleInfo(null);
            }
          } catch (settleErr) {
            console.warn(
              "⚠️ Could not load settleability info; using legacy gating:",
              settleErr
            );
            setSettleInfo(null);
          }

          // Apply filters
          applyFilters(allRequests, page);
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError("Failed to load payout requests");
        console.error("Error loading payout requests:", err);
      } finally {
        setLoading(false);
      }
    },
    [applyFilters, loadTransferStatuses, extractAvailableDates]
  );

  useEffect(() => {
    if (isAdmin || isOwner) {
      loadPayoutRequests();
    }
  }, [isAdmin, isOwner, loadPayoutRequests]);

  // Apply filters when filter criteria change or when data updates
  useEffect(() => {
    if (allPayoutRequests.length > 0) {
      applyFilters(allPayoutRequests, currentPage); // Maintain current page unless filters change
    }
  }, [allPayoutRequests, applyFilters, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (allPayoutRequests.length > 0) {
      applyFilters(allPayoutRequests, 1);
    }
  }, [
    dateFilterType,
    singleDate,
    dateRangeStart,
    dateRangeEnd,
    selectedStatus,
    allPayoutRequests,
    applyFilters,
  ]);

  // Real-time subscription for transfer status updates
  useEffect(() => {
    if (!isAdmin && !isOwner) return;

    console.log(
      "🔔 Setting up real-time subscriptions for transfer status updates"
    );

    // Subscribe to wise_transfer_status table changes
    const transferStatusSubscription = supabase
      .channel("transfer-status-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "wise_transfer_status",
        },
        async (payload) => {
          console.log("🔄 Transfer status update received:", payload);

          const { eventType, new: newRecord, old: oldRecord } = payload;

          if (eventType === "UPDATE" || eventType === "INSERT") {
            // Update the transfer status in our local state
            const formattedStatus =
              wiseWebhookService.formatTransferStatus(newRecord);

            setTransferStatuses((prev) => ({
              ...prev,
              [newRecord.transfer_id]: formattedStatus,
            }));

            // If the transfer status indicates completion or failure,
            // refresh the payout requests to get updated payout status
            if (
              formattedStatus?.display?.isCompleted ||
              formattedStatus?.display?.isFailed
            ) {
              console.log(
                "🔄 Transfer completed/failed, refreshing payout requests"
              );
              await loadPayoutRequests(currentPage);
            }
          }
        }
      )
      .subscribe();

    // Subscribe to payout_transactions table changes for real-time status updates
    const payoutSubscription = supabase
      .channel("payout-changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "payout_transaction",
        },
        (payload) => {
          console.log("🔄 Payout status update received:", payload);

          // Update the specific payout in our local state
          setAllPayoutRequests((prev) => {
            const updated = prev.map((request) =>
              request.id === payload.new.id
                ? { ...request, ...payload.new }
                : request
            );
            return updated;
          });
        }
      )
      .subscribe();

    setRealtimeSubscription({ transferStatusSubscription, payoutSubscription });

    return () => {
      console.log("🔌 Cleaning up real-time subscriptions");
      transferStatusSubscription.unsubscribe();
      payoutSubscription.unsubscribe();
    };
  }, [isAdmin, isOwner, currentPage, loadPayoutRequests]);

  // Load current payout mode on mount
  useEffect(() => {
    (async () => {
      const r = await payoutTransactionService.getPayoutMode();
      if (r.success) setPayoutMode(r.data);
    })();
  }, []);

  const handleToggleMode = async () => {
    const next = payoutMode === "manual" ? "automatic" : "manual";
    if (
      next === "automatic" &&
      !window.confirm(
        "Switch to AUTOMATIC payouts? Affiliates will be funded without manual approval."
      )
    )
      return;
    setModeLoading(true);
    const r = await payoutTransactionService.setPayoutMode(next);
    if (r.success) setPayoutMode(next);
    else setError(r.error);
    setModeLoading(false);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleExportCSV = () => {
    const exportData = filteredRequests.map((request) => ({
      "Request ID": request.id,
      "Affiliate Name": request.affiliate_name,
      Amount: request.amount,
      "Payment Method": request.payment_method,
      Status: request.status,
      "Request Date": new Date(request.request_date).toLocaleDateString(),
      Notes: request.notes || "",
      "Review Notes": request.review_notes || "",
      "Reviewed By": request.reviewer_profile?.username || "",
      "Reviewed At": request.reviewed_at
        ? new Date(request.reviewed_at).toLocaleDateString()
        : "",
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);

    // Generate filename with timestamp
    const now = new Date();
    const timestamp = now
      .toISOString()
      .replace(/[:.]/g, "-")
      .replace("T", "_")
      .split(".")[0];
    link.setAttribute("download", `${timestamp}_payouts.csv`);

    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleApprove = async (payoutId) => {
    setProcessingId(payoutId);
    setNotice(null);

    try {
      const result = await payoutTransactionService.approvePayoutRequest(
        payoutId,
        user.id,
        "Approved by admin"
      );

      // Small delay to ensure database has updated, then refresh
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await loadPayoutRequests(currentPage);

      if (result.pendingVerification) {
        // Funding was sent but Wise has not confirmed yet — informational,
        // not a failure. The cron/webhook will finalize it automatically.
        setError(null);
        setNotice({
          type: "warning",
          message:
            "Payment sent — Wise is still confirming funding; this will finalize automatically.",
        });
      } else if (result.success) {
        setError(null); // Clear any previous errors
        setNotice({
          type: "success",
          message: result.resumed
            ? "Transfer was already funded — payout has been marked as paid."
            : result.message || "Payout approved and processed successfully",
        });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to approve payout request");
      console.error("Error approving payout:", err);
      // Still refresh to show any status changes that might have occurred
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await loadPayoutRequests(currentPage);
    } finally {
      setProcessingId(null);
    }
  };

  // Re-check a stuck 'processing' row against Wise on demand — no status
  // update first; the edge function resumes/finalizes it if its bound
  // transfer already funded, instead of waiting for the next cron run.
  const handleVerifyPayment = async (payoutId) => {
    setProcessingId(payoutId);
    setNotice(null);

    try {
      const result =
        await payoutTransactionService.verifyPayoutPayment(payoutId);

      // Small delay to ensure database has updated, then refresh
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await loadPayoutRequests(currentPage);

      if (result.pendingVerification) {
        // Funding was sent but Wise has not confirmed yet — informational,
        // not a failure. The cron/webhook will finalize it automatically.
        setError(null);
        setNotice({
          type: "warning",
          message:
            "Payment sent — Wise is still confirming funding; this will finalize automatically.",
        });
      } else if (result.success) {
        setError(null); // Clear any previous errors
        setNotice({
          type: "success",
          message: result.resumed
            ? "Transfer was already funded — payout has been marked as paid."
            : result.message || "Payout verified and processed successfully",
        });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to verify payment");
      console.error("Error verifying payment:", err);
      // Still refresh to show any status changes that might have occurred
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await loadPayoutRequests(currentPage);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = (payoutId) => {
    setDeclinePayoutId(payoutId);
    setShowDeclineConfirm(true);
  };

  const confirmDecline = async () => {
    if (!declinePayoutId) return;

    setProcessingId(declinePayoutId);
    setShowDeclineConfirm(false);

    try {
      const result = await payoutTransactionService.declinePayoutRequest(
        declinePayoutId,
        user.id,
        "Declined by admin"
      );

      if (result.success) {
        await loadPayoutRequests(currentPage); // Refresh the current page
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to decline payout request");
      console.error("Error declining payout:", err);
    } finally {
      setProcessingId(null);
      setDeclinePayoutId(null);
    }
  };

  const cancelDecline = () => {
    setShowDeclineConfirm(false);
    setDeclinePayoutId(null);
  };

  const handleMarkAsPaid = async (payoutId) => {
    setProcessingId(payoutId);

    try {
      const result = await payoutTransactionService.markPayoutAsPaid(
        payoutId,
        user.id,
        "Marked as paid by admin"
      );

      if (result.success) {
        await loadPayoutRequests(currentPage); // Refresh the current page
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to mark payout as paid");
      console.error("Error marking as paid:", err);
    } finally {
      setProcessingId(null);
    }
  };

  // Settle an open request as paid OUTSIDE the system (no Wise transfer)
  const handleMarkPaidManual = (request) => {
    setManualPaidRequest(request);
    setShowManualPaidConfirm(true);
  };

  const confirmMarkPaidManual = async () => {
    if (!manualPaidRequest) return;

    setProcessingId(manualPaidRequest.id);
    setShowManualPaidConfirm(false);

    try {
      const result = await payoutTransactionService.recordManualPayout(
        manualPaidRequest.affiliate_name,
        0,
        "manual",
        "",
        null,
        [manualPaidRequest.id]
      );

      if (result.success) {
        setError(null);
        setNotice({
          type: "success",
          message: `Recorded ${formatCurrency(manualPaidRequest.amount)} request from ${manualPaidRequest.affiliate_name} as paid outside the system (no Wise transfer sent).`,
        });
        await loadPayoutRequests(currentPage); // Refresh the current page
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to record manual settlement");
      console.error("Error settling payout manually:", err);
    } finally {
      setProcessingId(null);
      setManualPaidRequest(null);
    }
  };

  const cancelMarkPaidManual = () => {
    setShowManualPaidConfirm(false);
    setManualPaidRequest(null);
  };

  // Helper: describe what happens next for a pending payout.
  // In MANUAL mode the row waits for a human — it is NOT auto-approved/paid.
  // Only AUTOMATIC mode promotes pending rows to approved after 24h.
  const getTimeUntilAutoApproval = (requestDate) => {
    if (payoutMode !== "automatic") {
      return "Awaiting manual approval";
    }

    const now = new Date();
    const request = new Date(requestDate);
    const autoApprovalTime = new Date(request.getTime() + 24 * 60 * 60 * 1000); // 24 hours later
    const timeRemaining = autoApprovalTime - now;

    if (timeRemaining <= 0) {
      return "Will be auto-approved & paid";
    }

    const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutes = Math.floor(
      (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
    );

    return `Auto-approve & pay in ${hours}h ${minutes}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-yellow-400 bg-yellow-400/10 border-yellow-400/20";
      case "approved":
        return "text-green-400 bg-green-400/10 border-green-400/20";
      case "declined":
        return "text-red-400 bg-red-400/10 border-red-400/20";
      case "paid":
        return "text-blue-400 bg-blue-400/10 border-blue-400/20";
      case "failed":
        return "text-red-500 bg-red-500/10 border-red-500/20";
      default:
        return "text-gray-400 bg-gray-400/10 border-gray-400/20";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "approved":
        return <CheckCircle className="h-4 w-4" />;
      case "declined":
        return <XCircle className="h-4 w-4" />;
      case "paid":
        return <DollarSign className="h-4 w-4" />;
      case "failed":
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const showDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const showTransferStatusHistory = async (transferId) => {
    try {
      console.log(`📋 Loading status history for transfer: ${transferId}`);
      const result =
        await wiseWebhookService.getTransferStatusHistory(transferId);

      if (result.success) {
        setStatusHistory({ [transferId]: result.data });
        setSelectedTransferId(transferId);
        setShowStatusHistory(true);
      } else {
        setError(`Failed to load status history: ${result.error}`);
      }
    } catch (error) {
      console.error("❌ Error loading status history:", error);
      setError("Failed to load transfer status history");
    }
  };

  if (!isAdmin && !isOwner) {
    return (
      <div className="flex items-center justify-center py-12">
        <Alert
          type="error"
          message="You don't have permission to access this page."
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 text-[#B8460E] mx-auto mb-4" />
          <p className="text-gray-400">Loading Payout Requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <main className="max-w-7xl mx-auto">
        <div className="space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <DollarSign className="h-6 w-6 text-[#B8460E]" />
                Payout Requests
              </h2>
              <p className="text-gray-400 mt-1 text-sm sm:text-base">
                Manage affiliate payout requests and approvals
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Real-time status indicator */}
              <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-400">Live Updates</span>
              </div>

              {/* Payout mode indicator + toggle */}
              <div
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${
                  payoutMode === "automatic"
                    ? "bg-amber-500/10 border-amber-500/40"
                    : "bg-white/10 border-white/20"
                }`}
              >
                <span
                  className={`text-xs font-medium ${
                    payoutMode === "automatic"
                      ? "text-amber-400"
                      : "text-gray-300"
                  }`}
                >
                  Mode: {payoutMode === "automatic" ? "Automatic" : "Manual"}
                </span>
                <Button
                  onClick={handleToggleMode}
                  disabled={modeLoading}
                  className={
                    payoutMode === "automatic"
                      ? "bg-white/10 hover:bg-white/20 text-white border-white/20"
                      : "bg-amber-600 hover:bg-amber-700 text-white border-amber-600"
                  }
                >
                  {payoutMode === "automatic"
                    ? "Switch to Manual"
                    : "Switch to Automatic"}
                </Button>
              </div>

              <button
                onClick={() => setShowManualPayout(!showManualPayout)}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white border rounded-lg transition-colors ${
                  showManualPayout
                    ? "bg-green-600 border-green-500 hover:bg-green-700"
                    : "bg-white/10 hover:bg-white/20 border-white/20"
                }`}
              >
                <Plus className="h-4 w-4 mr-2" />
                Record Manual Payout
                {showManualPayout ? (
                  <ChevronUp className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronDown className="h-4 w-4 ml-2" />
                )}
              </button>

              <button
                onClick={() => loadPayoutRequests(currentPage)}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>

          {/* Manual Payout Recorder */}
          {showManualPayout && (
            <div className="mb-6">
              <ManualPayoutRecorder
                onPayoutRecorded={() => {
                  loadPayoutRequests(currentPage);
                  setShowManualPayout(false);
                }}
              />
            </div>
          )}

          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 p-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Date Range Picker */}
              <CustomDateRangePicker
                dateFilterType={dateFilterType}
                setDateFilterType={setDateFilterType}
                singleDate={singleDate}
                setSingleDate={setSingleDate}
                dateRangeStart={dateRangeStart}
                setDateRangeStart={setDateRangeStart}
                dateRangeEnd={dateRangeEnd}
                setDateRangeEnd={setDateRangeEnd}
                availableDates={availableDates}
                compact={true}
                showLegend={false}
              />

              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Status:</span>
                <select
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                >
                  <option value="all" className="bg-gray-800">
                    All Status
                  </option>
                  <option value="pending" className="bg-gray-800">
                    Pending
                  </option>
                  <option value="approved" className="bg-gray-800">
                    Approved
                  </option>
                  <option value="declined" className="bg-gray-800">
                    Declined
                  </option>
                  <option value="paid" className="bg-gray-800">
                    Paid
                  </option>
                  <option value="failed" className="bg-gray-800">
                    Failed
                  </option>
                </select>
              </div>
            </div>

            {/* Export CSV Button */}
            <button
              onClick={handleExportCSV}
              disabled={filteredRequests.length === 0}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#B8460E] hover:bg-[#8B3A0E] border border-[#B8460E] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV ({filteredRequests.length})
            </button>
          </div>

          {error && (
            <Alert
              type="error"
              message={error}
              onClose={() => setError(null)}
            />
          )}

          {notice && (
            <Alert
              type={notice.type}
              message={notice.message}
              onClose={() => setNotice(null)}
            />
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Pending Requests</p>
                  <p className="text-2xl font-bold text-yellow-400">
                    {
                      allPayoutRequests.filter((r) => r.status === "pending")
                        .length
                    }
                  </p>
                </div>
                <div className="p-3 bg-yellow-400/10 rounded-full">
                  <Clock className="h-6 w-6 text-yellow-400" />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Approved</p>
                  <p className="text-2xl font-bold text-green-400">
                    {
                      allPayoutRequests.filter(
                        (r) => r.status === "approved" || r.status === "paid"
                      ).length
                    }
                  </p>
                </div>
                <div className="p-3 bg-green-400/10 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Total Amount</p>
                  <p className="text-2xl font-bold text-blue-400">
                    {formatCurrency(
                      allPayoutRequests
                        .filter((r) => r.status === "pending")
                        .reduce((sum, r) => sum + parseFloat(r.amount), 0)
                    )}
                  </p>
                </div>
                <div className="p-3 bg-blue-400/10 rounded-full">
                  <DollarSign className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Paid Out</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {formatCurrency(
                      allPayoutRequests
                        .filter((r) => r.status === "paid")
                        .reduce((sum, r) => sum + parseFloat(r.amount), 0)
                    )}
                  </p>
                </div>
                <div className="p-3 bg-purple-400/10 rounded-full">
                  <DollarSign className="h-6 w-6 text-purple-400" />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Declined</p>
                  <p className="text-2xl font-bold text-red-400">
                    {
                      allPayoutRequests.filter((r) => r.status === "declined")
                        .length
                    }
                  </p>
                </div>
                <div className="p-3 bg-red-400/10 rounded-full">
                  <XCircle className="h-6 w-6 text-red-400" />
                </div>
              </div>
            </Card>
          </div>

          {/* Payout Requests Table */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Payout Requests
            </h3>

            {payoutRequests.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400">No payout requests found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        Affiliate
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        Amount
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        Request Date
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutRequests.map((request) => (
                      <tr
                        key={request.id}
                        className="border-b border-white/5 hover:bg-white/5"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-white/10 rounded-full">
                              <User className="h-4 w-4 text-gray-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {request.affiliate_name}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-white font-semibold">
                            {formatCurrency(request.amount)}
                          </p>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex flex-col space-y-1">
                            <div
                              className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(request.status)}`}
                            >
                              {getStatusIcon(request.status)}
                              <span className="capitalize">
                                {request.status}
                              </span>
                            </div>

                            {/* Show transfer status if available */}
                            {request.transfer_id &&
                              transferStatuses[request.transfer_id] && (
                                <button
                                  onClick={() =>
                                    showTransferStatusHistory(
                                      request.transfer_id
                                    )
                                  }
                                  className="flex items-center space-x-1 text-xs hover:bg-white/10 px-2 py-1 rounded transition-colors"
                                  title="Click to view status history"
                                >
                                  <span>
                                    {
                                      transferStatuses[request.transfer_id]
                                        .display.icon
                                    }
                                  </span>
                                  <span
                                    className={`text-${transferStatuses[request.transfer_id].display.color}-400`}
                                  >
                                    {
                                      transferStatuses[request.transfer_id]
                                        .display.label
                                    }
                                  </span>
                                </button>
                              )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-300">
                                {new Date(
                                  request.request_date
                                ).toLocaleDateString()}
                              </span>
                            </div>
                            {request.status === "pending" && (
                              <div className="text-xs text-yellow-400">
                                {getTimeUntilAutoApproval(request.request_date)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => showDetails(request)}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 transition-colors"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Details
                            </button>

                            {request.status === "pending" && (
                              <>
                                <button
                                  onClick={() => handleApprove(request.id)}
                                  disabled={processingId === request.id}
                                  className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleDecline(request.id)}
                                  disabled={processingId === request.id}
                                  className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Decline
                                </button>
                              </>
                            )}

                            {request.status === "approved" && (
                              <button
                                onClick={() => handleMarkAsPaid(request.id)}
                                disabled={
                                  processingId === request.id ||
                                  Boolean(request.wise_transaction_id)
                                }
                                title={
                                  request.wise_transaction_id
                                    ? "Bound to a live Wise transfer — cannot be settled manually"
                                    : undefined
                                }
                                className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <DollarSign className="h-3 w-3 mr-1" />
                                Mark Paid
                              </button>
                            )}

                            {request.status === "processing" && (
                              <button
                                onClick={() => handleVerifyPayment(request.id)}
                                disabled={processingId === request.id}
                                title="Re-check this payout against Wise now instead of waiting for the next scheduled run"
                                className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Verify payment
                              </button>
                            )}

                            {["pending", "approved", "processing"].includes(
                              request.status
                            ) && (
                              <button
                                onClick={() => handleMarkPaidManual(request)}
                                disabled={
                                  processingId === request.id ||
                                  (settleInfo
                                    ? settleInfo[request.id]?.settleable !==
                                      true
                                    : Boolean(request.wise_transaction_id))
                                }
                                title={
                                  settleInfo
                                    ? settleInfo[request.id]?.settleable ===
                                      true
                                      ? "Record as paid without sending a Wise transfer"
                                      : settleInfo[request.id]
                                          ?.settle_block_reason ||
                                        "Not currently settleable — refresh the list"
                                    : request.wise_transaction_id
                                    ? "Bound to a live Wise transfer — cannot be settled manually"
                                    : "Record as paid without sending a Wise transfer"
                                }
                                className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <DollarSign className="h-3 w-3 mr-1" />
                                Mark Paid (manual)
                              </button>
                            )}

                            {request.status === "failed" && (
                              <button
                                onClick={() => handleApprove(request.id)}
                                disabled={processingId === request.id}
                                className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Retry
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Details Modal */}
          {showDetailsModal && selectedRequest && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-900 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white">
                    Payout Request Details
                  </h3>
                  <Button
                    onClick={() => setShowDetailsModal(false)}
                    variant="secondary"
                    className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                  >
                    Close
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 text-sm">Affiliate</label>
                      <p className="text-white font-medium">
                        {selectedRequest.affiliate_name}
                      </p>
                    </div>
                    <div>
                      <label className="text-gray-400 text-sm">Amount</label>
                      <p className="text-white font-medium">
                        {formatCurrency(selectedRequest.amount)}
                      </p>
                    </div>
                    <div>
                      <label className="text-gray-400 text-sm">
                        Payment Method
                      </label>
                      <p className="text-white font-medium capitalize">
                        {selectedRequest.payment_method.replace("_", " ")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-gray-400 text-sm">Status</label>
                      <div
                        className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(selectedRequest.status)}`}
                      >
                        {getStatusIcon(selectedRequest.status)}
                        <span className="capitalize">
                          {selectedRequest.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedRequest.notes && (
                    <div>
                      <label className="text-gray-400 text-sm">Notes</label>
                      <p className="text-white bg-white/5 rounded-lg p-3 mt-1">
                        {selectedRequest.notes}
                      </p>
                    </div>
                  )}

                  {selectedRequest.review_notes && (
                    <div>
                      <label className="text-gray-400 text-sm">
                        Review Notes
                      </label>
                      <p className="text-white bg-white/5 rounded-lg p-3 mt-1">
                        {selectedRequest.review_notes}
                      </p>
                    </div>
                  )}

                  {/* Wise fee breakdown — admin-only, only for new Wise payouts
                      that carry the grossed-up details. Old/manual rows lack
                      these fields, so this block simply doesn't render. */}
                  {selectedRequest.payment_details &&
                    selectedRequest.payment_details.wise_target_amount != null &&
                    selectedRequest.payment_details.wise_source_amount != null && (
                      <div>
                        <label className="text-gray-400 text-sm">
                          Wise Breakdown
                        </label>
                        <div className="text-white bg-white/5 rounded-lg p-3 mt-1 space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">
                              Affiliate received
                            </span>
                            <span>
                              {(() => {
                                const amt =
                                  selectedRequest.payment_details
                                    .wise_target_amount;
                                const code =
                                  selectedRequest.payment_details
                                    .wise_target_currency;
                                try {
                                  return new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: code,
                                  }).format(amt);
                                } catch (e) {
                                  return `${amt} ${code}`;
                                }
                              })()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Wise fee</span>
                            <span>
                              {formatCurrency(
                                selectedRequest.payment_details.wise_fee
                              )}{" "}
                              USD
                            </span>
                          </div>
                          {selectedRequest.payment_details
                            .wise_fee_gross_up_amount != null && (
                            <div className="flex justify-between">
                              <span className="text-gray-400">
                                Extra covered
                              </span>
                              <span>
                                {formatCurrency(
                                  selectedRequest.payment_details
                                    .wise_fee_gross_up_amount
                                )}{" "}
                                USD
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between font-medium">
                            <span className="text-gray-400">Total paid</span>
                            <span>
                              {formatCurrency(
                                selectedRequest.payment_details.wise_source_amount
                              )}{" "}
                              USD
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Wise-style row that lacks a grossed-up breakdown
                      (manual/legacy/failed row) — surface a small muted hint
                      instead of nothing. No row ever stores payment_method ===
                      "wise"; the affiliate flow stores a per-country method key,
                      so we detect a Wise row from markers that actually exist:
                      any Wise field in payment_details or a top-level
                      wise_transaction_id. */}
                  {(selectedRequest.payment_details?.wise_transfer_id != null ||
                    selectedRequest.payment_details?.wise_recipient_id != null ||
                    selectedRequest.payment_details?.auto_payout != null ||
                    selectedRequest.wise_transaction_id != null) &&
                    (selectedRequest.payment_details == null ||
                      selectedRequest.payment_details.wise_target_amount ==
                        null ||
                      selectedRequest.payment_details.wise_source_amount ==
                        null) && (
                      <p className="text-gray-500 text-sm italic">
                        No Wise breakdown recorded (manual/legacy/failed row)
                      </p>
                    )}

                  <div>
                    <label className="text-gray-400 text-sm">
                      Request Date
                    </label>
                    <p className="text-white bg-white/5 rounded-lg p-3 mt-1">
                      {new Date(selectedRequest.request_date).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transfer Status History Modal */}
          {showStatusHistory && selectedTransferId && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-900 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-white">
                    Transfer Status History
                  </h3>
                  <Button
                    onClick={() => setShowStatusHistory(false)}
                    variant="secondary"
                    className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                  >
                    Close
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="text-sm text-gray-400 mb-4">
                    Transfer ID: {selectedTransferId}
                  </div>

                  {statusHistory[selectedTransferId] &&
                  statusHistory[selectedTransferId].length > 0 ? (
                    <div className="space-y-3">
                      {statusHistory[selectedTransferId].map((event, index) => (
                        <div
                          key={index}
                          className="bg-white/5 rounded-lg p-4 border border-white/10"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                              <span className="text-white font-medium capitalize">
                                {event.current_state?.replace(/_/g, " ")}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400">
                              {new Date(event.occurred_at).toLocaleString()}
                            </span>
                          </div>

                          {event.previous_state && (
                            <div className="text-sm text-gray-400 mb-2">
                              From: {event.previous_state.replace(/_/g, " ")}
                            </div>
                          )}

                          {event.webhook_data && (
                            <div className="text-xs text-gray-500 bg-black/20 rounded p-2 mt-2">
                              <pre className="whitespace-pre-wrap">
                                {JSON.stringify(event.webhook_data, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      No status history available for this transfer
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Decline Confirmation Modal */}
          {showDeclineConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-900 rounded-xl p-6 max-w-md w-full">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>

                  <h3 className="text-lg font-medium text-white mb-2">
                    Decline Payout Request
                  </h3>

                  <p className="text-gray-300 mb-6">
                    Are you sure you want to decline this payout request? This
                    action cannot be undone.
                  </p>

                  <div className="flex justify-center gap-3">
                    <Button
                      variant="secondary"
                      onClick={cancelDecline}
                      className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={confirmDecline}
                      disabled={processingId === declinePayoutId}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      {processingId === declinePayoutId
                        ? "Declining..."
                        : "Yes, Decline"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mark Paid (manual) Confirmation Modal */}
          {showManualPaidConfirm && manualPaidRequest && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-900 rounded-xl p-6 max-w-md w-full">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
                    <DollarSign className="h-6 w-6 text-purple-600" />
                  </div>

                  <h3 className="text-lg font-medium text-white mb-2">
                    Mark Paid (Manual)
                  </h3>

                  <p className="text-gray-300 mb-3">
                    Record the {formatCurrency(manualPaidRequest.amount)}{" "}
                    request from {manualPaidRequest.affiliate_name} as paid{" "}
                    <span className="font-semibold">
                      WITHOUT sending any Wise transfer
                    </span>
                    ?
                  </p>

                  <p className="text-gray-400 text-sm mb-6">
                    Only use this for money already paid outside the system. No
                    funds will move.
                  </p>

                  <div className="flex justify-center gap-3">
                    <Button
                      variant="secondary"
                      onClick={cancelMarkPaidManual}
                      className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={confirmMarkPaidManual}
                      disabled={processingId === manualPaidRequest.id}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {processingId === manualPaidRequest.id
                        ? "Recording..."
                        : "Yes, Mark Paid"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          {totalRequests > PAGE_SIZE && (
            <div className="flex justify-between items-center mt-6">
              <button
                className="px-4 py-2 rounded-lg bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => applyFilters(allPayoutRequests, currentPage - 1)}
                disabled={currentPage === 1 || loading}
              >
                Previous
              </button>
              <span className="text-gray-300">
                Page {currentPage} of {Math.ceil(totalRequests / PAGE_SIZE)} •{" "}
                {totalRequests} total requests
              </span>
              <button
                className="px-4 py-2 rounded-lg bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => applyFilters(allPayoutRequests, currentPage + 1)}
                disabled={
                  currentPage >= Math.ceil(totalRequests / PAGE_SIZE) || loading
                }
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PayoutRequesters;
